/* eslint-disable camelcase */
import { datadogLogs } from '@datadog/browser-logs';
import { pickBy } from 'ramda';
import { createStore } from 'zustand/vanilla';

import type { LoadTableConfigResponse } from '@ezugi/types';
import { queryParams } from '@ezugi/utils/functions/queryParams/queryParams';
import { DeviceInfo } from '@ezugi/utils/helpers/deviceInfo';
import { getOrientation } from '@ezugi/utils/helpers/getOrientation';
import isFullscreen from '@ezugi/utils/predicates/isFullscreen';

import { getCurrentFPS } from './fpsTracker';

import type { ABTestingPayload, DataLayerEvent } from './types';

type AnalyticsStore = {
  datadogEnabled: boolean;
  enabled: boolean;
  setEnabled: (value: boolean) => void;
  setDatadogEnabled: (value: boolean) => void;
  basePayload: Partial<DataLayerEvent>;
  setBasePayload: (data?: LoadTableConfigResponse['result']) => void;
  abTestingPayload?: ABTestingPayload;
  setABTestingPayload: (params: ABTestingPayload) => void;
};

const defaultStaticEventParams = {
  // @ts-ignore
  // eslint-disable-next-line no-undef
  gameVersion: __TAG_VERSION,
} as const;

export const analyticsStore = createStore<AnalyticsStore>((set) => ({
  datadogEnabled: false,
  setDatadogEnabled: (datadogEnabled: boolean) => set(() => ({ datadogEnabled })),
  enabled: false,
  setEnabled: (enabled: boolean) => set(() => ({ enabled })),
  basePayload: defaultStaticEventParams,
  setBasePayload: (data?: LoadTableConfigResponse['result']) => set(() => {
    const basePayload: Partial<DataLayerEvent> = {
      ...defaultStaticEventParams,
    };

    const { playerId, tableId } = queryParams.getQueryParams();

    basePayload.playerId = data?.userData?.uid ?? playerId;

    basePayload.dealer_id = data?.tables?.[tableId]?.dealer_id as string;
    // client_id comes wrong from PHP Proxy, instead of tableId, it contains "lobby"
    basePayload.clientId = data?.userData?.client_id
      .split('|')
      .map((value, index) => (index === 2 ? tableId : value))
      .join('|');

    if (data?.userData?.session_id) {
      basePayload.ezugi_session_id = data.userData.session_id;
    }

    return { basePayload };
  }),
  clearBasePayload: () => set(() => ({ basePayload: {} })),
  setABTestingPayload: (abTestingPayload: ABTestingPayload) => set(() => ({ abTestingPayload })),
}));

const hasOwn = (object: object, property: PropertyKey) => Object.prototype.hasOwnProperty.call(object, property);

const withTimeProperties = (eventData: DataLayerEvent) => {
  const hasCreatedAt = hasOwn(eventData, 'created_at');
  const hasLocalTime = hasOwn(eventData, 'local_time');

  if (hasCreatedAt && hasLocalTime) return eventData;

  const date = new Date();

  return {
    ...eventData,
    created_at: eventData.created_at || date.toUTCString(),
    local_time: eventData.local_time || date.toString(),
  };
};

const withABTestingPayload = () => {
  const { abTestingPayload } = analyticsStore.getState();
  const candidatePayload = {
    ui_version: abTestingPayload?.uiVersion,
    matka_auto_place_bet: abTestingPayload?.matkaAutoPlaceBet,
    matka_hide_numbers_grid_until_bet_placed: abTestingPayload?.matkaHideNumbersGridUntilBetPlaced,
  };
  return pickBy<
    typeof candidatePayload,
    Partial<typeof candidatePayload>
  >((value) => value !== undefined, candidatePayload);
};

/*
  * Main function to send analytics events. It composes the event payload with static and dynamic properties,
  * and pushes it to the dataLayer for Google Analytics, as well as logging it to DataDog.
  * The eventData parameter allows passing custom properties for the specific event,
  * while the function adds common properties like timestamps and device info.
  * The deferUntilIdle flag allows deferring the event sending until the browser is idle, to avoid impacting performance during critical interactions.
*/
export const sendAnalytics = (eventData: DataLayerEvent, deferUntilIdle: boolean = false) => {
  const sendEvent = () => {
    const { basePayload } = analyticsStore.getState();

    const currentQueryParams = queryParams.getQueryParams();

    const dynamicDefaultParams = {
      operatorId: currentQueryParams.operatorId,
      tableId: currentQueryParams.tableId,
      resolution: `${window.innerWidth},${window.innerHeight}`,
      full_screen: +isFullscreen(),
      ...(DeviceInfo.isHandheld && {
        pos: getOrientation(),
      }),
      custom_ezugi_timestamp: Date.now(),
      fps: getCurrentFPS(),
    };
    const event: DataLayerEvent = {
      ...basePayload,
      ...withTimeProperties(eventData),
      ...withABTestingPayload(),
      ...dynamicDefaultParams,
    };

    // NOTE: DataDog Custom logging pinched in Google Analytics logic, part of the fading out of Google Analytics.
    datadogLogs.logger.info(event.event ?? 'Google Analytics Event', event);

    if (window.dataLayer) {
      window.dataLayer.push(event);
    }
  };

  if (deferUntilIdle) {
    'requestIdleCallback' in window
      ? requestIdleCallback(sendEvent)
      : setTimeout(sendEvent, 0);
  } else {
    sendEvent();
  }
};
